import { Text } from "@asayinc/component-library";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import { LoadingCard } from "src/components/molecules/Loading";
import { useLocationAnalysisEventQuery } from "src/store/locationAnalysisEvent";

export function LocationAnalysisEventStatsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: locationAnalysisEvent,
    isLoading,
    error,
  } = useLocationAnalysisEventQuery({ id });

  if (error || (!isLoading && !locationAnalysisEvent)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const { locationAnalysisEventStats: stats } = locationAnalysisEvent;

  if (!stats) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <Card title="Broker Stats">
              <Text
                variant="body2"
                sx={{ color: "text.secondary", py: 5, px: 4 }}
              >
                No stats available
              </Text>
            </Card>
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <Card title="Invoice Information">
              <Text
                variant="body2"
                sx={{ color: "text.secondary", py: 5, px: 4 }}
              >
                No invoice stats available
              </Text>
            </Card>
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const {
    eligibleParentBrokers,
    eligibleBrokers,
    respondedParentBrokers,
    awaitingParentBrokers,
    totalPositions,
    totalCustomers,
    totalShares,
    giftedPositions,
    giftedCustomers,
    giftedShares,
    fullSharePositions,
    fullShareCustomers,
    fullShareShares,
    nonFullSharePositions,
    nonFullShareCustomers,
    nonFullShareShares,
    invoiceDate,
    invoiceAmount,
    estimatedInvoiceAmount,
  } = stats;

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Card title="Broker Stats">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Eligible Parent Brokers"
              value={eligibleParentBrokers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Eligible Brokers"
              value={eligibleBrokers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Responded Parent Brokers"
              value={respondedParentBrokers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Awaiting Parent Brokers"
              value={awaitingParentBrokers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                fontWeight: 600,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Total Customers"
              value={totalCustomers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Full Share Customers"
              value={fullShareCustomers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Fractional Customers"
              value={nonFullShareCustomers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Gifted Customers"
              value={giftedCustomers.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                fontWeight: 600,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Total Positions"
              value={totalPositions.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Full Share Positions"
              value={fullSharePositions.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Fractional Positions"
              value={nonFullSharePositions.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Gifted Positions"
              value={giftedPositions.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                fontWeight: 600,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Total Shares"
              value={totalShares.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Full Share Shares"
              value={fullShareShares.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Fractional Shares"
              value={nonFullShareShares.toString()}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Gifted Shares"
              value={giftedShares.toString()}
            />
          </Card>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Card title="Invoice information">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Invoice date"
              value={
                invoiceDate ? dayjs(invoiceDate).format("MM/DD/YYYY") : "N/A"
              }
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Invoice amount"
              value={`$${(invoiceAmount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Estimated invoice amount"
              value={`$${(estimatedInvoiceAmount || 0).toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}`}
            />
          </Card>
        </Box>
      </Box>
    </ScrollContainer>
  );
}
